import { AnyPayload } from '../State';
import { duration } from '../utils/duration';
import { chatbotStaticAssets } from './chatbotStaticAssets';
import { chatbotMessageTemplates } from './messageTemplates';

/**
 * Payload for chatbot messsages
 * We should use it for all future chatbot messages ChatbotMessageSuccess/ChatbotMessageFailure
 */
export type ChatbotMessagePayload = {
  $channel: string;
  feature: string;
  $subFeature: string;
  $creativeAssetID?: string;
  $creativeCTA?: string;
  $creativeTextID?: string;
  contextID?: string;
  carouselMessages?: number;
  template?: string;
} & AnyPayload;

/**
 * Properties for chatbot static messages.
 * Static messages are the ones we are using for open web, in opposition to the flex messages,
 * which are the original ones used for Line platform.
 */
export type ChatbotStaticMessageArgs = {
  title: string;
  subtitle?: string;
  cta?: string;
  imageUrl?: string;
  state: any;
  api: any;
  payload: ChatbotMessagePayload;
  template?: string;
  text?: string;
  quickReplies?: Record<string, any>[];
  quickReplyButtons?: Record<string, any>[];
  analyticsUserProperties?: {
    [key: string]: unknown;
  };
};

/**
 * Use this function for generation chatbot payload pn replicant side
 */
export function generateReplicantChatbotPayload(
  payload: Omit<ChatbotMessagePayload, '$channel' | 'sourcePayload'>,
): ChatbotMessagePayload {
  // It will override same properties from GCInstant.getPlatformPayloadData(),
  const messagePayload = {
    $channel: 'CHATBOT',
    feature: payload.feature,
    $subFeature: payload.$subFeature,
    $creativeAssetID: payload.$creativeAssetID,
    $creativeCTA: payload.$creativeCTA,
    $creativeTextID: payload.$creativeTextID,
    contextID: payload.contextID,
    templateID: payload.templateID,
  };

  return {
    ...messagePayload,
  };
}

/**
 * Generate a login token for the chatbot message payload
 */
export async function createLoginToken(api: any) {
  if (api.getUserID() === 'offline-player') return 'dummy-login-token';
  const token = await api.loginLinks.createLoginToken({
    expiresInMs: duration({ days: 30 }),
  });

  return token;
}

/**
 * Build chatbot message payload
 */
export function generateChatbotPayload(
  opts: ChatbotMessagePayload & {
    login?: {
      token: string;
      userId?: string;
    };
  },
) {
  let payload: any = {
    ...opts,
    $channel: 'CHATBOT',
  };

  if (opts.login) {
    payload = {
      ...payload,
      $loginToken: {
        userId: opts.login.userId,
        token: opts.login.token,
      },
    };
  }

  return payload;
}

/**
 * Generate a url for chatbot messages
 */
export function generateChatbotUrl(
  platform: string,
  payload: any,
  amplitudeKey: string,
  userId: string,
) {
  const finalUrl = new URL(
    `https://${process.env.STAGE === 'dev' ? 'dev--' : ''}${
      process.env.APP_ID
    }.playco.games`,
  );
  finalUrl.searchParams.set('payload', JSON.stringify(payload));

  // Redirect through https://github.com/play-co/webview-escape-hatch
  const escapeUrl = new URL(
    'https://frxk7e0wmj.execute-api.us-east-1.amazonaws.com/escape',
  );
  escapeUrl.searchParams.set('amplitudeKey', amplitudeKey);
  escapeUrl.searchParams.set('userId', userId);
  escapeUrl.searchParams.set('url', finalUrl.href);

  // Workaround for FB bug: Messenger on mobile appends the doubly-encoded string
  // "%2526device_id%253D(UUID)" to our URL for whatever godforsaken reason. In order to prevent
  // this from corrupting the above params needed for our redirect, we add an unused dummy parameter
  // as our last URL parameter, which can be freely corrupted by Messenger.
  escapeUrl.searchParams.set('unused', 'x');

  return escapeUrl.href;
}

/**
 * Assemble a chatbot message based on a static image, for web
 * 1 - Create login token (createLoginToken)
 * 2 - Generate chatbot payload (generateChatbotPayload)
 * 3 - Generate chatbot url (generateChatbotUrl)
 * 4 - Build and return the web message (template)
 */
export async function getChatbotStaticMessage(
  params: ChatbotStaticMessageArgs,
) {
  let forceReceiverId = '';
  let forcePlatform = '';

  // Force platform and user id for local development only
  if (process.env.REPLICANT_ENDPOINT?.includes('localhost')) {
    if (process.env.APP_ID === 'TREMBLY') {
      forceReceiverId = process.env.INSTAGRAM_CHATBOT_RECEIVER_ID;
      forcePlatform = 'instagram';
    } else {
      forceReceiverId = process.env.MESSENGER_CHATBOT_RECEIVER_ID;
      forcePlatform = 'messenger';
    }
  }

  const templateName = params.template || 'messengerPlayButton';
  if (!chatbotMessageTemplates[templateName]) {
    throw new Error(`Unknown chatbot message template: ${templateName}`);
  }

  const imageUrl = params.imageUrl || 'static/generic';
  if (imageUrl.startsWith('static/') && !chatbotStaticAssets[imageUrl]) {
    throw new Error(`Unknown chatbot static image: ${imageUrl}`);
  }

  const template = chatbotMessageTemplates[templateName];
  const userId = forceReceiverId || params.state.id;
  const platform = forcePlatform || params.state.platform;
  const token = await createLoginToken(params.api);
  const payload = generateChatbotPayload({
    ...params.payload,
    login: {
      userId,
      token,
    },
  });
  const url = generateChatbotUrl(
    platform,
    payload,
    process.env.AMPLITUDE,
    userId,
  );
  const args = {
    imageUrl,
    title: params.title,
    subtitle: params.subtitle || '',
    cta: params.cta || 'Play Now!',
    ...(params.text && { text: params.text }),
    ...(params.quickReplies && { quickReplies: params.quickReplies }),
    ...(params.quickReplyButtons && {
      quickReplyButtons: params.quickReplyButtons,
    }),
    url,
  };
  const analyticsUserProperties = params.analyticsUserProperties ?? {};
  const webMessage = template({
    args: { ...args },
    payload: { ...payload },
    analyticsUserProperties,
  });

  if (forcePlatform) webMessage.forcePlatform = forcePlatform;
  if (forceReceiverId) webMessage.forceReceiverId = forceReceiverId;

  return webMessage;
}

/**
 * Send a chatbot message based on current chatbot platform
 * @param api Replicant API
 * @param receiverId User id of the recipient
 * @param message The message object
 * @param platform The chatbot platform
 */
export async function sendChatbotMessage(
  api: any,
  receiverId: string,
  message: any,
  platform: string,
): Promise<void> {
  // Log the message in development mode, for debugging purposes
  if (process.env.STAGE === 'dev') {
    logMessage(receiverId, message, platform);
  }

  switch (platform) {
    case 'instagram':
      api.chatbot.sendInstagramMessage(receiverId, message);
      break;
    case 'messenger':
      api.chatbot.sendMessengerMessage(receiverId, message);
      break;
    default:
      platform = platform || 'undefined';
      throw new Error(`Chatbot message failed. Unknown platform: ${platform}`);
  }
}

/**
 * Log the chatbot message in a more visual way, for development only
 */
function logMessage(receiverId: string, message: any, platform: string) {
  console.log(
    '[sendChatbotMessage]',
    `receiver: ${receiverId}`,
    `platform: ${platform}`,
    message,
  );
  const args = message.args || {};
  const list: string[] = ['===== CHATBOT MESSAGE ====='];
  if (message?.name) list.push(`TEMPLATE: ${message.name}`);
  if (args?.imageUrl) list.push(`IMAGE: ${args?.imageUrl}`);
  if (args?.title) list.push(`TITLE: ${args?.title}`);
  if (args?.subtitle) list.push(`SUBTITLE: ${args?.subtitle}`);
  if (args?.cta) list.push(`CTA: ${args?.cta}`);
  if (args?.quickReplyButtons) {
    list.push(
      `BUTTONS: ${args?.quickReplyButtons
        .map((b: any) => `${b.title}`)
        .join(' | ')}`,
    );
  }
  if (args?.quickReplies) {
    list.push(
      `BUTTONS: ${args?.quickReplies
        .map((b: any) => `${b.title}`)
        .join(' | ')}`,
    );
  }
  list.push('===========================');
  console.log(list.join('\n'));
}
